import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Star, FileText } from "lucide-react";
import { useConfirm } from "../../components/ConfirmDialog";

const EMPTY = { title: "", content: "", is_default: false, sort_order: 0 };

export default function AdminTerms() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();

  const load = () => api.get("/admin/terms-templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      const payload = {
        title: data.title.trim(),
        content: data.content,
        is_default: !!data.is_default,
        sort_order: Number(data.sort_order) || 0,
      };
      if (data.id) {
        await api.patch(`/admin/terms-templates/${data.id}`, payload);
        toast.success("Plantilla actualizada");
      } else {
        await api.post("/admin/terms-templates", payload);
        toast.success("Plantilla creada");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    }
  };

  const remove = async (t) => {
    const ok = await confirm({
      title: "Eliminar plantilla",
      message: `¿Eliminar "${t.title}"? Esta acción no se puede deshacer.`,
      confirmLabel: "Eliminar",
      tone: "danger",
    });
    if (!ok) return;
    await api.delete(`/admin/terms-templates/${t.id}`);
    toast.success("Eliminada");
    load();
  };

  return (
    <div className="p-6 md:p-8" data-testid="admin-terms">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-brand font-mono mb-1">Administración</div>
          <h1 className="font-display font-bold text-2xl md:text-3xl text-ash-100 flex items-center gap-3">
            <FileText size={24} className="text-brand" strokeWidth={1.5} />
            Términos y Condiciones
          </h1>
          <p className="text-ash-300 text-sm mt-2">
            Plantillas reutilizables que pueden insertarse en las propuestas. Marca una como predeterminada para que aparezca primero.
          </p>
        </div>
        <button
          data-testid="terms-new"
          onClick={() => setEditing({ ...EMPTY })}
          className="h-11 inline-flex items-center gap-2 bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 uppercase tracking-wider text-xs"
        >
          <Plus size={14} /> Nueva plantilla
        </button>
      </div>

      <div className="bg-ink-800 border border-ink-500">
        {items.length === 0 ? (
          <div className="p-10 text-center text-ash-500 text-sm">
            Aún no hay plantillas. Crea la primera con el botón de arriba.
          </div>
        ) : (
          <ul>
            {items.map((t) => (
              <li
                key={t.id}
                data-testid={`terms-row-${t.id}`}
                className="border-b border-ink-500 last:border-0 px-5 py-4 flex items-start gap-4 hover:bg-ink-700/30"
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-display font-semibold text-ash-100">{t.title}</span>
                    {t.is_default && (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 bg-brand/15 text-brand border border-brand/30">
                        <Star size={9} fill="currentColor" strokeWidth={0} /> Predeterminada
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-widest text-ash-500 font-mono ml-2">orden: {t.sort_order}</span>
                  </div>
                  <pre className="text-xs text-ash-300 whitespace-pre-wrap font-sans line-clamp-3">{t.content}</pre>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    onClick={() => setEditing({ ...t })}
                    title="Editar"
                    className="p-2 border border-ink-500 hover:border-brand hover:text-brand text-ash-300"
                  >
                    <Pencil size={13} />
                  </button>
                  <button
                    onClick={() => remove(t)}
                    title="Eliminar"
                    className="p-2 border border-ink-500 hover:border-[#9B1C1C] hover:text-[#FF7B7B] text-ash-300"
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {editing && (
        <TermsModal
          template={editing}
          onSave={save}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function TermsModal({ template, onSave, onClose }) {
  const [form, setForm] = useState(template);
  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const inputCls = "bg-ink-700 border border-ink-500 text-ash-100 focus:border-brand outline-none px-3 py-2 text-sm w-full placeholder:text-ash-500";

  return (
    <div
      className="fixed inset-0 bg-ink-900/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-ink-800 border border-ink-500 w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-500 px-5 py-3">
          <h3 className="font-display font-semibold text-ash-100">
            {form.id ? "Editar plantilla" : "Nueva plantilla"}
          </h3>
          <button onClick={onClose} className="p-1 text-ash-300 hover:text-ash-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <Field label="Título">
            <input
              data-testid="terms-title"
              value={form.title}
              onChange={(e) => update("title", e.target.value)}
              placeholder="Ej. Plan mensual SaaS"
              className={inputCls}
            />
          </Field>
          <Field label="Contenido">
            <textarea
              data-testid="terms-content"
              rows={10}
              value={form.content}
              onChange={(e) => update("content", e.target.value)}
              placeholder="Texto completo de los términos y condiciones..."
              className={inputCls + " font-mono text-xs"}
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Orden de aparición">
              <input
                type="number"
                value={form.sort_order}
                onChange={(e) => update("sort_order", e.target.value)}
                className={inputCls}
              />
            </Field>
            <label className="flex items-end gap-2 cursor-pointer pb-2">
              <input
                type="checkbox"
                data-testid="terms-default"
                checked={!!form.is_default}
                onChange={(e) => update("is_default", e.target.checked)}
                className="accent-brand"
              />
              <span className="text-sm text-ash-100">Marcar como predeterminada</span>
            </label>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-ink-500 px-5 py-3">
          <button onClick={onClose} className="border border-ink-500 hover:border-ash-300 text-ash-300 px-4 py-2 text-xs uppercase tracking-widest">Cancelar</button>
          <button
            data-testid="terms-save"
            onClick={() => onSave(form)}
            disabled={!form.title.trim() || !form.content.trim()}
            className="bg-brand text-ink-800 font-semibold hover:bg-brand-hover px-5 py-2 text-xs uppercase tracking-widest disabled:opacity-40"
          >
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-widest text-ash-500 font-mono mb-1">{label}</div>
      {children}
    </label>
  );
}
